import React, { useState, useEffect, useRef } from 'react';
import { Eye, Plus, Edit, Trash2, ChevronLeft, ChevronRight, ArrowLeft, Check, X, Calendar, DollarSign, ShoppingCart, CreditCard, Building, Phone, Download } from 'lucide-react';
import html2pdf from 'html2pdf.js';
// CSS handled by global theme.css
import Sidebar from '../../components/Sidebar';
import Header from '../../components/Header';
import apiService from '../../utils/api';
import { useToast } from '../../components/Toast';
const TODAY_DATE = new Date().toISOString().split('T')[0];

function CustomerPage() {
  const toast = useToast();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [currentView, setCurrentView] = useState('list'); // 'list', 'form', 'detail', 'payment-form'
  const [editingItem, setEditingItem] = useState(null);
  const [viewingItem, setViewingItem] = useState(null);
  const [customerBankDetails, setCustomerBankDetails] = useState([]);
  const [showBankForm, setShowBankForm] = useState(false);
  const [editingBankDetail, setEditingBankDetail] = useState(null);
  
  const [bankFormData, setBankFormData] = useState({
    bank_name: '',
    account_number: '',
    ifsc_code: '',
    account_holder_name: '',
    account_type: 'savings',
    upi_id: '',
    is_primary: false
  });
  
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    cow_milk_price: '',
    buffalo_milk_price: '',
    chick_price: '',
    fatwa_price: '',
    payment_mode: 'cash',
    billing_period: '30',
    billing_period_enabled: true,
    payment_terms: '30',
    payment_terms_enabled: true,
    phone_number: '',
    bank_name: '',
    account_number: '',
    ifsc_code: '',
    account_holder_name: '',
    upi_id: '',
    vehicle_number: ''
  });

  // Payment form data
  const [paymentFormData, setPaymentFormData] = useState({
    amount: '',
    payment_date: new Date().toISOString().split('T')[0],
    payment_mode: 'cash',
    cash_amount: '',
    bank_amount: '',
    transaction_id: '',
    reference_number: '',
    notes: '',
    status: 'completed'
  });

  // Detail view data
  const [customerSummary, setCustomerSummary] = useState(null);
  const [customerInvoices, setCustomerInvoices] = useState([]);
  const [customerPayments, setCustomerPayments] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);

  // Invoice view / download modal
  const [viewInvoice, setViewInvoice] = useState(null);
  const [viewInvoiceLoading, setViewInvoiceLoading] = useState(false);
  const [pendingDownload, setPendingDownload] = useState(false);
  const invoiceViewRef = useRef(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  // Search filters state
  const [filters, setFilters] = useState({
    customer_id: '',
    name: '',
    address: '',
    payment_mode: ''
  });

  const loadCustomerBankDetails = async (customerId) => {
    try {
      const response = await apiService.getCustomerBankDetails(customerId);
      setCustomerBankDetails(response.data || []);
    } catch (err) {
      console.error('Error loading bank details:', err);
    }
  };

  // Load customers from backend
  const loadCustomers = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.getCustomers();
      setData(response.data || []);
    } catch (err) {
      console.error('Error loading customers:', err);
      setError('Failed to load customers: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadCustomerDetailData = async (customerId) => {
    try {
      setDetailLoading(true);
      setError(null);

      // Load customer summary, invoices, payments, and bank details in parallel
      const [summaryResponse, invoicesResponse, paymentsResponse] = await Promise.all([
        apiService.getCustomerSummary(customerId),
        apiService.getInvoices({ customer_id: customerId }),
        apiService.getCustomerPayments(customerId, { per_page: 10 })
      ]);

      setCustomerSummary(summaryResponse);
      setCustomerInvoices(invoicesResponse.data || []);
      setCustomerPayments(paymentsResponse.data || []);
      
      // Load bank details separately
      await loadCustomerBankDetails(customerId);
    } catch (err) {
      console.error('Error loading customer details:', err);
      setError('Failed to load customer details: ' + err.message);
    } finally {
      setDetailLoading(false);
    }
  };

  // Load data on component mount
  useEffect(() => {
    loadCustomers();
  }, []);

  // Filter data based on search filters
  const filteredData = data.filter(item => {
    return (
      (filters.customer_id === '' || item.customer_id.toString().includes(filters.customer_id)) &&
      (filters.name === '' || item.name.toLowerCase().includes(filters.name.toLowerCase())) &&
      (filters.address === '' || (item.address && item.address.toLowerCase().includes(filters.address.toLowerCase()))) &&
      (filters.payment_mode === '' || (item.payment_mode && item.payment_mode.toLowerCase().includes(filters.payment_mode.toLowerCase())))
    );
  });

  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentItems = filteredData.slice(startIndex, startIndex + itemsPerPage);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filters]);

  const handleAdd = () => {
    setEditingItem(null);
    setFormData({
      name: '',
      address: '',
      cow_milk_price: '',
      buffalo_milk_price: '',
      chick_price: '',
      fatwa_price: '',
      payment_mode: 'cash',
      billing_period: '30',
      billing_period_enabled: true,
      payment_terms: '30',
      payment_terms_enabled: true,
      phone_number: '',
      bank_name: '',
      account_number: '',
      ifsc_code: '',
      account_holder_name: '',
      upi_id: '',
      vehicle_number: ''
    });
    setCurrentView('form');
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      name: item.name || '',
      address: item.address || '',
      cow_milk_price: item.cow_milk_price ? item.cow_milk_price.toString() : '',
      buffalo_milk_price: item.buffalo_milk_price ? item.buffalo_milk_price.toString() : '',
      chick_price: item.chick_price ? item.chick_price.toString() : '',
      fatwa_price: item.fatwa_price ? item.fatwa_price.toString() : '',
      payment_mode: item.payment_mode || 'cash',
      billing_period: item.billing_period ? item.billing_period.toString() : '30',
      billing_period_enabled: item.billing_period_enabled !== false,
      payment_terms: item.payment_terms ? item.payment_terms.toString() : '30',
      payment_terms_enabled: item.payment_terms_enabled !== false,
      phone_number: item.phone_number || '',
      bank_name: item.bank_name || '',
      account_number: item.account_number || '',
      ifsc_code: item.ifsc_code || '',
      account_holder_name: item.account_holder_name || '',
      upi_id: item.upi_id || '',
      vehicle_number: item.vehicle_number || ''
    });
    setCurrentView('form');
  };

  const handleView = async (item) => {
    setViewingItem(item);
    setCurrentView('detail');
    await loadCustomerDetailData(item.customer_id);
  };

  const handleAddPayment = () => {
    setPaymentFormData({
      amount: '',
      payment_date: new Date().toISOString().split('T')[0],
      payment_mode: 'cash',
      cash_amount: '',
      bank_amount: '',
      transaction_id: '',
      reference_number: '',
      notes: '',
      status: 'completed'
    });
    setCurrentView('payment-form');
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this customer?')) {
      try {
        setLoading(true);
        await apiService.deleteCustomer(id);
        await loadCustomers(); // Reload data
        
        // Adjust pagination if necessary
        const newTotalPages = Math.ceil((filteredData.length - 1) / itemsPerPage);
        if (currentPage > newTotalPages && newTotalPages > 0) {
          setCurrentPage(newTotalPages);
        }
      } catch (err) {
        console.error('Error deleting customer:', err);
        toast.error('Failed to delete customer: ' + err.message);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleSubmit = async () => {
    // Validation
    if (!formData.name.trim()) {
      toast.warning('Customer name is required.');
      return;
    }

    // Price validation
    if (formData.cow_milk_price && (isNaN(formData.cow_milk_price) || Number(formData.cow_milk_price) < 0)) {
      toast.warning('Please enter a valid cow milk price.');
      return;
    }

    if (formData.buffalo_milk_price && (isNaN(formData.buffalo_milk_price) || Number(formData.buffalo_milk_price) < 0)) {
      toast.warning('Please enter a valid buffalo milk price.');
      return;
    }

    if (formData.billing_period_enabled && formData.billing_period && (isNaN(formData.billing_period) || Number(formData.billing_period) <= 0)) {
      toast.warning('Please enter a valid billing period.');
      return;
    }

    if (formData.vehicle_number.trim() && !/^\d+$/.test(formData.vehicle_number.trim())) {
      toast.warning('Vehicle number must contain digits only.');
      return;
    }

    const submissionData = {
      name: formData.name.trim(),
      address: formData.address.trim() || null,
      cow_milk_price: formData.cow_milk_price ? Number(formData.cow_milk_price) : null,
      buffalo_milk_price: formData.buffalo_milk_price ? Number(formData.buffalo_milk_price) : null,
      chick_price: formData.chick_price ? Number(formData.chick_price) : null,
      fatwa_price: formData.fatwa_price ? Number(formData.fatwa_price) : null,
      payment_mode: formData.payment_mode || null,
      billing_period_enabled: formData.billing_period_enabled,
      billing_period: formData.billing_period_enabled && formData.billing_period ? Number(formData.billing_period) : null,
      payment_terms_enabled: formData.payment_terms_enabled,
      payment_terms: formData.payment_terms_enabled ? (formData.payment_terms ? Number(formData.payment_terms) : 30) : null,
      phone_number: formData.phone_number.trim() || null,
      bank_name: formData.bank_name.trim() || null,
      account_number: formData.account_number.trim() || null,
      ifsc_code: formData.ifsc_code.trim() || null,
      account_holder_name: formData.account_holder_name.trim() || null,
      upi_id: formData.upi_id.trim() || null,
      vehicle_number: formData.vehicle_number.trim() || null
    };

    try {
      setLoading(true);
      setError(null);

      if (editingItem) {
        await apiService.updateCustomer(editingItem.customer_id, submissionData);
      } else {
        await apiService.createCustomer(submissionData);
      }
      
      await loadCustomers(); // Reload data
      setCurrentView('list');
      setFormData({
        name: '',
        address: '',
        cow_milk_price: '',
        buffalo_milk_price: '',
        chick_price: '',
        fatwa_price: '',
        payment_mode: 'cash',
        billing_period: '30',
        billing_period_enabled: true,
        payment_terms: '30',
        payment_terms_enabled: true,
        phone_number: '',
        bank_name: '',
        account_number: '',
        ifsc_code: '',
        account_holder_name: '',
        upi_id: ''
      });
      setEditingItem(null);
    } catch (err) {
      console.error('Error saving customer:', err);
      toast.error('Failed to save customer: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentSubmit = async () => {
    // Validation
    if (!paymentFormData.amount || isNaN(paymentFormData.amount) || Number(paymentFormData.amount) <= 0) {
      toast.warning('Please enter a valid payment amount.');
      return;
    }

    if (!paymentFormData.payment_date) {
      toast.warning('Please select a payment date.');
      return;
    }

    const totalAmount = Number(paymentFormData.amount);
    const cashAmount = paymentFormData.cash_amount ? Number(paymentFormData.cash_amount) : 0;
    const bankAmount = paymentFormData.bank_amount ? Number(paymentFormData.bank_amount) : 0;

    if (paymentFormData.payment_mode === 'both' && Math.abs((cashAmount + bankAmount) - totalAmount) > 0.01) {
      toast.warning('Cash + Bank must equal total amount when mode is "Both".');
      return;
    }

    const submissionData = {
      amount: totalAmount,
      payment_date: paymentFormData.payment_date,
      payment_mode: paymentFormData.payment_mode,
      cash_amount: paymentFormData.payment_mode === 'cash' ? totalAmount : (paymentFormData.payment_mode === 'both' ? cashAmount : 0),
      bank_amount: (paymentFormData.payment_mode === 'bank' || paymentFormData.payment_mode === 'upi') ? totalAmount : (paymentFormData.payment_mode === 'both' ? bankAmount : 0),
      bank_charges: (paymentFormData.payment_mode !== 'cash' && paymentFormData.bank_charges) ? Number(paymentFormData.bank_charges) : 0,
      transaction_id: paymentFormData.transaction_id.trim() || null,
      reference_number: paymentFormData.reference_number.trim() || null,
      notes: paymentFormData.notes.trim() || null,
      status: paymentFormData.status
    };

    try {
      setLoading(true);
      setError(null);

      await apiService.addCustomerPayment(viewingItem.customer_id, submissionData);
      
      // Reload customer details
      await loadCustomerDetailData(viewingItem.customer_id);
      
      setCurrentView('detail');
      setPaymentFormData({
        amount: '',
        payment_date: new Date().toISOString().split('T')[0],
        payment_mode: 'cash',
        cash_amount: '',
        bank_amount: '',
        bank_charges: '',
        transaction_id: '',
        reference_number: '',
        notes: '',
        status: 'completed'
      });
    } catch (err) {
      console.error('Error adding payment:', err);
      toast.error('Failed to add payment: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handlePaymentInputChange = (e) => {
    const { name, value } = e.target;
    setPaymentFormData({
      ...paymentFormData,
      [name]: value
    });

    if (name === 'payment_mode') {
      if (value === 'cash') {
        setPaymentFormData(prev => ({ ...prev, [name]: value, cash_amount: prev.amount, bank_amount: '', bank_charges: '' }));
      } else if (value === 'bank' || value === 'upi') {
        setPaymentFormData(prev => ({ ...prev, [name]: value, cash_amount: '', bank_amount: prev.amount }));
      } else if (value === 'both') {
        setPaymentFormData(prev => ({ ...prev, [name]: value, cash_amount: '', bank_amount: '' }));
      }
    }

    if (name === 'amount') {
      if (paymentFormData.payment_mode === 'cash') {
        setPaymentFormData(prev => ({ ...prev, [name]: value, cash_amount: value }));
      } else if (paymentFormData.payment_mode === 'bank' || paymentFormData.payment_mode === 'upi') {
        setPaymentFormData(prev => ({ ...prev, [name]: value, bank_amount: value }));
      }
    }
  };

  const handleAddBankDetail = () => {
    setEditingBankDetail(null);
    setBankFormData({
      bank_name: '',
      account_number: '',
      ifsc_code: '',
      account_holder_name: '',
      account_type: 'savings',
      upi_id: '',
      is_primary: customerBankDetails.length === 0 // Set as primary if it's the first one
    });
    setShowBankForm(true);
  };
  
  const handleEditBankDetail = (bankDetail) => {
    setEditingBankDetail(bankDetail);
    setBankFormData({
      bank_name: bankDetail.bank_name || '',
      account_number: bankDetail.account_number || '',
      ifsc_code: bankDetail.ifsc_code || '',
      account_holder_name: bankDetail.account_holder_name || '',
      account_type: bankDetail.account_type || 'savings',
      upi_id: bankDetail.upi_id || '',
      is_primary: bankDetail.is_primary || false
    });
    setShowBankForm(true);
  };
  
  const handleBankFormSubmit = async () => {
    try {
      setLoading(true);
      setError(null);
  
      if (editingBankDetail) {
        await apiService.updateCustomerBankDetail(
          viewingItem.customer_id, 
          editingBankDetail.bank_detail_id, 
          bankFormData
        );
      } else {
        await apiService.addCustomerBankDetail(viewingItem.customer_id, bankFormData);
      }
      
      // Reload bank details
      await loadCustomerBankDetails(viewingItem.customer_id);
      
      setShowBankForm(false);
      setBankFormData({
        bank_name: '',
        account_number: '',
        ifsc_code: '',
        account_holder_name: '',
        account_type: 'savings',
        upi_id: '',
        is_primary: false
      });
      setEditingBankDetail(null);
    } catch (err) {
      console.error('Error saving bank detail:', err);
      toast.error('Failed to save bank detail: ' + err.message);
    } finally {
      setLoading(false);
    }
  };
  
  const handleDeleteBankDetail = async (bankDetailId) => {
    if (window.confirm('Are you sure you want to delete this bank detail?')) {
      try {
        setLoading(true);
        await apiService.deleteCustomerBankDetail(viewingItem.customer_id, bankDetailId);
        await loadCustomerBankDetails(viewingItem.customer_id);
      } catch (err) {
        console.error('Error deleting bank detail:', err);
        toast.error('Failed to delete bank detail: ' + err.message);
      } finally {
        setLoading(false);
      }
    }
  };
  
  const handleSetPrimaryBank = async (bankDetailId) => {
    try {
      setLoading(true);
      await apiService.setPrimaryBankDetail(viewingItem.customer_id, bankDetailId);
      await loadCustomerBankDetails(viewingItem.customer_id);
    } catch (err) {
      console.error('Error setting primary bank:', err);
      toast.error('Failed to set primary bank: ' + err.message);
    } finally {
      setLoading(false);
    }
  };
  
  const handleBankInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setBankFormData({
      ...bankFormData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleFilterChange = (e) => {
    setFilters({
      ...filters,
      [e.target.name]: e.target.value
    });
  };

  const handleCancel = () => {
    setCurrentView('list');
    setFormData({
      name: '',
      address: '',
      cow_milk_price: '',
      buffalo_milk_price: '',
      chick_price: '',
      fatwa_price: '',
      payment_mode: 'cash',
      billing_period: '30',
      billing_period_enabled: true,
      payment_terms: '30',
      payment_terms_enabled: true,
      phone_number: '',
      bank_name: '',
      account_number: '',
      ifsc_code: '',
      account_holder_name: '',
      upi_id: '',
      vehicle_number: ''
    });
    setPaymentFormData({
      amount: '',
      payment_date: new Date().toISOString().split('T')[0],
      payment_mode: 'cash',
      cash_amount: '',
      bank_amount: '',
      transaction_id: '',
      reference_number: '',
      notes: '',
      status: 'completed'
    });
    setEditingItem(null);
    setViewingItem(null);
    setCustomerSummary(null);
    setCustomerInvoices([]);
    setCustomerPayments([]);
    setError(null);
  };

  const handleBackToDetail = () => {
    setCurrentView('detail');
    setError(null);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const clearFilters = () => {
    setFilters({
      customer_id: '',
      name: '',
      address: '',
      payment_mode: ''
    });
  };

  const formatCurrency = (amount) => {
    return amount ? `₹${parseFloat(amount).toFixed(2)}` : '₹0.00';
  };

  /* ── invoice view / download ─────────────────────────────────────────── */
  const handleViewInvoice = async (invoiceId) => {
    try {
      setViewInvoiceLoading(true);
      const res = await apiService.getInvoice(invoiceId);
      setViewInvoice(res.data);
    } catch (err) {
      toast.error('Failed to load invoice: ' + err.message);
    } finally {
      setViewInvoiceLoading(false);
    }
  };

  const handleDownloadInvoice = async (invoiceId) => {
    setPendingDownload(true);
    await handleViewInvoice(invoiceId);
  };

  useEffect(() => {
    if (viewInvoice && pendingDownload && invoiceViewRef.current) {
      html2pdf().set({
        margin: 0.5,
        filename: `Invoice_${viewInvoice.invoice_number}_${viewInvoice.customer_name || ''}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true, ignoreElements: (el) => el.classList && el.classList.contains('print-hide') },
        jsPDF: { unit: 'in', format: 'a4', orientation: 'portrait' },
        pagebreak: { mode: ['css', 'legacy'] }
      }).from(invoiceViewRef.current).save();
      setPendingDownload(false);
    }
  }, [viewInvoice, pendingDownload]);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN');
  };

  const renderLayout = (content) => (
    <div className="app-container">
      <Header />
      <div className="main-content">
        <Sidebar />
        <div className="page-content">
          {content}
        </div>
      </div>
    </div>
  );

  if (loading && data.length === 0) {
    return renderLayout(
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading customers...</p>
      </div>
    );
  }

  // Payment Form View
  if (currentView === 'payment-form') {
    return renderLayout(
      <>
        <div className="page-header">
           <div className="header-left">
            <button className="btn btn-secondary" onClick={handleBackToDetail} style={{ marginRight: '1rem' }}>
              <ArrowLeft size={18} /> Back
            </button>
            <h1 className="page-title">
               Add Payment - {viewingItem?.name}
            </h1>
           </div>
        </div>

        {error && (
            <div className="alert alert-error">
                <p>{error}</p>
            </div>
        )}

        <div className="card" style={{ maxWidth: '600px', margin: '0 auto' }}>
            <div className="form-group">
                <label htmlFor="amount" className="form-label">Total Amount *</label>
                <div style={{ position: 'relative' }}>
                    <span style={{ position: 'absolute', left: '10px', top: '10px', color: 'var(--text-secondary)' }}>₹</span>
                    <input
                        type="number"
                        id="amount"
                        name="amount"
                        className="form-input"
                        value={paymentFormData.amount}
                        onChange={handlePaymentInputChange}
                        placeholder="Enter amount"
                        min="0"
                        step="0.01"
                        style={{ paddingLeft: '25px' }}
                        disabled={loading}
                    />
                </div>
            </div>

            <div className="form-group">
                <label htmlFor="payment_date" className="form-label">Payment Date *</label>
                <input
                    type="date" max={TODAY_DATE}
                    id="payment_date"
                    name="payment_date"
                    className="form-input"
                    value={paymentFormData.payment_date}
                    onChange={handlePaymentInputChange}
                    disabled={loading}
                />
            </div>

            <div className="form-group">
                <label htmlFor="payment_mode" className="form-label">Payment Mode *</label>
                <select
                    id="payment_mode"
                    name="payment_mode"
                    className="form-select"
                    value={paymentFormData.payment_mode}
                    onChange={handlePaymentInputChange}
                    disabled={loading}
                >
                    <option value="cash">Cash</option>
                    <option value="bank">Bank</option>
                    <option value="upi">UPI</option>
                    <option value="both">Both (Cash + Bank)</option>
                </select>
            </div>

            {paymentFormData.payment_mode === 'both' && (
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Cash Portion</label>
                  <input type="number" name="cash_amount" className="form-input" value={paymentFormData.cash_amount} onChange={handlePaymentInputChange} min="0" step="0.01" disabled={loading} />
                </div>
                <div className="form-group">
                  <label className="form-label">Bank Portion</label>
                  <input type="number" name="bank_amount" className="form-input" value={paymentFormData.bank_amount} onChange={handlePaymentInputChange} min="0" step="0.01" disabled={loading} />
                </div>
              </div>
            )}

            {paymentFormData.payment_mode !== 'cash' && (
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Bank Charges (optional)</label>
                  <input type="number" name="bank_charges" className="form-input" value={paymentFormData.bank_charges || ''} onChange={handlePaymentInputChange} placeholder="0.00" min="0" step="0.01" disabled={loading} />
                </div>
                <div className="form-group">
                  <label htmlFor="transaction_id" className="form-label">Transaction ID</label>
                  <input type="text" id="transaction_id" name="transaction_id" className="form-input" value={paymentFormData.transaction_id} onChange={handlePaymentInputChange} placeholder="Enter transaction ID" disabled={loading} />
                </div>
              </div>
            )}

            <div className="form-group">
                <label htmlFor="reference_number" className="form-label">Reference Number</label>
                <input
                    type="text"
                    id="reference_number"
                    name="reference_number"
                    className="form-input"
                    value={paymentFormData.reference_number}
                    onChange={handlePaymentInputChange}
                    placeholder="Optional"
                    disabled={loading}
                />
            </div>

            <div className="form-group">
                <label htmlFor="status" className="form-label">Payment Status</label>
                <select
                    id="status"
                    name="status"
                    className="form-select"
                    value={paymentFormData.status}
                    onChange={handlePaymentInputChange}
                    disabled={loading}
                >
                    <option value="completed">Completed</option>
                    <option value="pending">Pending</option>
                    <option value="failed">Failed</option>
                </select>
            </div>

            <div className="form-group">
                <label htmlFor="notes" className="form-label">Notes</label>
                <textarea
                    id="notes"
                    name="notes"
                    className="form-textarea"
                    value={paymentFormData.notes}
                    onChange={handlePaymentInputChange}
                    placeholder="Enter any additional notes"
                    rows="3"
                    disabled={loading}
                />
            </div>

            <div className="form-actions" style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem', marginTop: '2rem' }}>
                <button type="button" className="btn btn-secondary" onClick={handleBackToDetail} disabled={loading}>
                    Cancel
                </button>
                <button type="button" className="btn btn-primary" onClick={handlePaymentSubmit} disabled={loading}>
                     <Check size={18} />
                    {loading ? 'Adding...' : 'Add Payment'}
                </button>
            </div>
        </div>
      </>
    );
  }

  // Customer Detail View
  if (currentView === 'detail') {
    const customer = viewingItem;
    const summary = customerSummary?.summary || {};
    
    return renderLayout(
      <>
        <div className="page-header">
           <div className="header-left">
            <button className="btn btn-secondary" onClick={handleCancel} style={{ marginRight: '1rem' }}>
              <ArrowLeft size={18} /> Back
            </button>
            <h1 className="page-title">
                {customer.name}
            </h1>
           </div>
           <button className="btn btn-primary" onClick={() => handleEdit(customer)}>
                <Edit size={18} /> Edit Profile
            </button>
        </div>

        {error && (
            <div className="alert alert-error">
                <p>{error}</p>
            </div>
        )}
        
        <div className="form-row">
            <div className="card" style={{ flex: 1 }}>
                <div className="card-header">
                    <h3 className="card-title">Customer Profile</h3>
                </div>
                 <div className="detail-grid" style={{ gridTemplateColumns: '1fr 1fr' }}>
                    <div className="form-group">
                        <label className="form-label">Address</label>
                        <div>{customer.address || 'N/A'}</div>
                    </div>
                    <div className="form-group">
                        <label className="form-label">Phone</label>
                         <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <Phone size={16} color="var(--text-secondary)" />
                            {customer.phone_number || 'N/A'}
                         </div>
                    </div>
                     <div className="form-group">
                        <label className="form-label">Payment Mode</label>
                         <div>
                            <span className={`status-badge ${customer.payment_mode === 'cash' ? 'available' : 'unavailable'}`}>
                                {customer.payment_mode || 'Cash'}
                            </span>
                        </div>
                    </div>
                     <div className="form-group">
                        <label className="form-label">Billing</label>
                        <div>{customer.billing_period_enabled ? `${customer.billing_period} Days` : 'Not enabled'}</div>
                    </div>
                    <div className="form-group">
                        <label className="form-label">Cow Milk Price</label>
                        <div style={{ color: 'var(--primary-color)', fontWeight: '600' }}>₹{customer.cow_milk_price || 'Standard'}</div>
                    </div>
                    <div className="form-group">
                        <label className="form-label">Buffalo Milk Price</label>
                        <div style={{ color: 'var(--primary-color)', fontWeight: '600' }}>₹{customer.buffalo_milk_price || 'Standard'}</div>
                    </div>
                    <div className="form-group">
                        <label className="form-label">Chick Price</label>
                        <div style={{ color: 'var(--primary-color)', fontWeight: '600' }}>{customer.chick_price ? `₹${customer.chick_price}` : 'Not set'}</div>
                    </div>
                    <div className="form-group">
                        <label className="form-label">Fatwa Price</label>
                        <div style={{ color: 'var(--primary-color)', fontWeight: '600' }}>{customer.fatwa_price ? `₹${customer.fatwa_price}` : 'Not set'}</div>
                    </div>
                    <div className="form-group">
                        <label className="form-label">Payment Terms</label>
                        <div style={{ color: 'var(--primary-color)', fontWeight: '600' }}>{customer.payment_terms_enabled ? `${customer.payment_terms || 30} days` : 'Not enabled'}</div>
                    </div>
                    <div className="form-group">
                        <label className="form-label">Vehicle Number</label>
                        <div>{customer.vehicle_number || 'Not set'}</div>
                    </div>
                 </div>
            </div>

             <div className="card" style={{ flex: 1 }}>
                <div className="card-header">
                    <h3 className="card-title">Financial Summary</h3>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '1rem' }}>
                     <div className="stat-box" style={{ background: '#ecfccb', padding: '1rem', borderRadius: '8px', textAlign: 'center' }}>
                        <div style={{ fontSize: '0.9rem', color: '#4d7c0f' }}>Total Purchased</div>
                        <div style={{ fontSize: '1.75rem', fontWeight: '700', color: '#3f6212' }}>
                            {formatCurrency(summary.total_purchased)}
                        </div>
                    </div>
                    <div className="stat-box" style={{ background: '#f0f9ff', padding: '1rem', borderRadius: '8px', textAlign: 'center' }}>
                        <div style={{ fontSize: '0.9rem', color: '#0369a1' }}>Total Paid</div>
                         <div style={{ fontSize: '1.75rem', fontWeight: '700', color: '#075985' }}>
                            {formatCurrency(summary.total_paid)}
                        </div>
                    </div>
                     <div className="stat-box" style={{ background: summary.balance < 0 ? '#fee2e2' : '#dcfce7', padding: '1rem', borderRadius: '8px', textAlign: 'center' }}>
                        <div style={{ fontSize: '0.9rem', color: summary.balance < 0 ? '#991b1b' : '#166534' }}>Current Balance</div>
                         <div style={{ fontSize: '1.75rem', fontWeight: '700', color: summary.balance < 0 ? '#b91c1c' : '#15803d' }}>
                            {formatCurrency(Math.abs(summary.balance))} {summary.balance < 0 ? 'Dr' : 'Cr'}
                        </div>
                    </div>
                </div>
             </div>
        </div>
        
        <div className="card" style={{ marginTop: '2rem' }}>
             <div className="card-header">
                <h3 className="card-title">Bank Details</h3>
                <button className="btn btn-secondary" onClick={handleAddBankDetail}>
                    <Plus size={16} /> Add Bank
                </button>
            </div>
             <div className="table-container" style={{ margin: 0, boxShadow: 'none', border: 'none' }}>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Bank Name</th>
                      <th>Account No</th>
                      <th>Recepient</th>
                      <th>IFSC</th>
                      <th>UPI ID</th>
                      <th>Primary</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {customerBankDetails.map(bank => (
                      <tr key={bank.bank_detail_id}>
                        <td>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <Building size={16} color="var(--text-secondary)" />
                                {bank.bank_name}
                            </div>
                        </td>
                        <td>{bank.account_number}</td>
                         <td>{bank.account_holder_name}</td>
                        <td>{bank.ifsc_code}</td>
                         <td>{bank.upi_id}</td>
                        <td>
                            {bank.is_primary ? (
                                <span className="status-badge available" style={{ fontSize: '0.75rem' }}>Primary</span>
                            ) : (
                                <button className="btn-icon" onClick={() => handleSetPrimaryBank(bank.bank_detail_id)} title="Set as Primary">
                                    <Check size={16} />
                                </button>
                            )}
                        </td>
                        <td>
                           <div className="actions-cell">
                            <button className="btn-icon" onClick={() => handleEditBankDetail(bank)}>
                              <Edit size={16} />
                            </button>
                            <button className="btn-icon" style={{ color: 'var(--error-color)' }} onClick={() => handleDeleteBankDetail(bank.bank_detail_id)}>
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                 {customerBankDetails.length === 0 && (
                  <div className="no-data">
                    <p>No bank details added.</p>
                  </div>
                )}
            </div>
        </div>

        <div className="form-row" style={{ marginTop: '2rem' }}>
            {/* Invoices */}
             <div className="card" style={{ flex: 1 }}>
                <div className="card-header">
                    <h3 className="card-title">Invoices</h3>
                </div>
                 <div className="table-container" style={{ margin: 0, boxShadow: 'none', border: 'none', maxHeight: '300px', overflowY: 'auto' }}>
                    <table className="data-table">
                        <thead>
                            <tr>
                                <th>Invoice No</th>
                                <th>Date</th>
                                <th>Amount</th>
                                <th>Status</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                       <tbody>
                        {customerInvoices.map(inv => (
                            <tr key={inv.invoice_id}>
                                <td style={{ fontWeight: 600 }}>{inv.invoice_number}</td>
                                <td>{formatDate(inv.invoice_date)}</td>
                                <td style={{ fontWeight: 600 }}>{formatCurrency(inv.total_amount)}</td>
                                <td>
                                     <span className={`status-badge ${inv.status === 'paid' ? 'available' : 'unavailable'}`}>
                                        {inv.status}
                                    </span>
                                </td>
                                <td>
                                    <div className="actions-cell">
                                        <button className="btn-icon" onClick={() => handleViewInvoice(inv.invoice_id)} disabled={viewInvoiceLoading} title="View"><Eye size={16} /></button>
                                        <button className="btn-icon" onClick={() => handleDownloadInvoice(inv.invoice_id)} disabled={viewInvoiceLoading} title="Download PDF"><Download size={16} /></button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                       </tbody>
                    </table>
                     {customerInvoices.length === 0 && (
                        <div className="no-data"><p>No invoices created yet</p></div>
                    )}
                 </div>
            </div>

            {/* Payment History */}
            <div className="card" style={{ flex: 1 }}>
                <div className="card-header">
                    <h3 className="card-title">Payment History</h3>
                    <button className="btn btn-primary" onClick={handleAddPayment}>
                        <DollarSign size={16} /> Record Payment
                    </button>
                </div>
                 <div className="table-container" style={{ margin: 0, boxShadow: 'none', border: 'none', maxHeight: '300px', overflowY: 'auto' }}>
                    <table className="data-table">
                        <thead>
                            <tr>
                                <th>Date</th>
                                <th>Amount</th>
                                <th>Mode</th>
                                <th>Status</th>
                            </tr>
                        </thead>
                       <tbody>
                        {customerPayments.map(payment => (
                            <tr key={payment.payment_id}>
                                <td>{formatDate(payment.payment_date)}</td>
                                <td style={{ fontWeight: 600, color: 'var(--primary-color)' }}>{formatCurrency(payment.amount)}</td>
                                <td>{payment.payment_mode}</td>
                                <td>
                                     <span className={`status-badge ${payment.status === 'completed' ? 'available' : 'pending'}`}>
                                        {payment.status}
                                    </span>
                                </td>
                            </tr>
                        ))}
                       </tbody>
                    </table>
                     {customerPayments.length === 0 && (
                        <div className="no-data"><p>No payment history</p></div>
                    )}
                 </div>
            </div>
        </div>
        
        {/* Bank Form Modal */}
        {showBankForm && (
            <div className="modal-overlay">
                <div className="modal-content">
                    <div className="modal-header">
                        <h3>{editingBankDetail ? 'Edit Bank Detail' : 'Add Bank Detail'}</h3>
                        <button className="btn-icon" onClick={() => setShowBankForm(false)}>
                            <X size={20} />
                        </button>
                    </div>
                     <div className="modal-body">
                         <div className="form-group">
                             <label className="form-label">Bank Name *</label>
                             <input type="text" name="bank_name" className="form-input" value={bankFormData.bank_name} onChange={handleBankInputChange} />
                         </div>
                         <div className="form-group">
                             <label className="form-label">Account No *</label>
                             <input type="text" name="account_number" className="form-input" value={bankFormData.account_number} onChange={handleBankInputChange} />
                         </div>
                         <div className="form-row">
                             <div className="form-group">
                                 <label className="form-label">IFSC Code</label>
                                 <input type="text" name="ifsc_code" className="form-input" value={bankFormData.ifsc_code} onChange={handleBankInputChange} />
                             </div>
                             <div className="form-group">
                                 <label className="form-label">Account Type</label>
                                  <select name="account_type" className="form-select" value={bankFormData.account_type} onChange={handleBankInputChange}>
                                    <option value="savings">Savings</option>
                                    <option value="current">Current</option>
                                  </select>
                             </div>
                         </div>
                         <div className="form-group">
                             <label className="form-label">Account Holder Name</label>
                             <input type="text" name="account_holder_name" className="form-input" value={bankFormData.account_holder_name} onChange={handleBankInputChange} />
                         </div>
                          <div className="form-group">
                             <label className="form-label">UPI ID</label>
                             <input type="text" name="upi_id" className="form-input" value={bankFormData.upi_id} onChange={handleBankInputChange} />
                         </div>
                          <div className="form-group" style={{ display: 'flex', alignItems: 'center' }}>
                             <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', gap: '0.5rem' }}>
                                <input
                                    type="checkbox"
                                    name="is_primary"
                                    checked={bankFormData.is_primary}
                                    onChange={handleBankInputChange}
                                    style={{ width: '1.2rem', height: '1.2rem' }}
                                />
                                <span style={{ fontSize: '1rem', fontWeight: '500' }}>Set as Primary</span>
                             </label>
                        </div>
                     </div>
                     <div className="modal-footer">
                        <button className="btn btn-secondary" onClick={() => setShowBankForm(false)}>Cancel</button>
                        <button className="btn btn-primary" onClick={handleBankFormSubmit}>
                            {editingBankDetail ? 'Update' : 'Add'}
                        </button>
                    </div>
                </div>
            </div>
        )}

        {viewInvoice && (
          <div className="modal-overlay" onClick={() => setViewInvoice(null)}>
            <div className="modal-content" style={{ maxWidth: '750px', maxHeight: '85vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <h3 style={{ margin: 0 }}>Invoice {viewInvoice.invoice_number}</h3>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button className="btn btn-secondary" onClick={() => handleDownloadInvoice(viewInvoice.invoice_id)}>
                    <Download size={16} /> Download PDF
                  </button>
                  <button className="btn-icon" onClick={() => setViewInvoice(null)} aria-label="Close"><X size={18} /></button>
                </div>
              </div>

              <div ref={invoiceViewRef} className="bill-preview">
                <div className="bill-container">
                  <div className="bill-header">
                    {viewInvoice.dairy_farm_fssai_number && <div className="dairy-fssai">FSSAI No: {viewInvoice.dairy_farm_fssai_number}</div>}
                    {viewInvoice.dairy_farm_phone && (
                      <div className="dairy-phone">
                        Phone Number: {viewInvoice.dairy_farm_phone.split(',').map(p => p.trim()).filter(Boolean).map((n, i) => (
                          <React.Fragment key={i}>{i > 0 && <br />}{n}</React.Fragment>
                        ))}
                      </div>
                    )}
                    <div className="dairy-name">{viewInvoice.dairy_farm_name || '—'}</div>
                    {viewInvoice.dairy_farm_description && <div className="dairy-description">{viewInvoice.dairy_farm_description}</div>}
                    {viewInvoice.dairy_farm_address && <div className="dairy-address">{viewInvoice.dairy_farm_address}</div>}
                  </div>

                  <div className="bill-info" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', margin: '1rem 0', padding: '0.8rem', background: '#f8fafc', borderRadius: '6px' }}>
                    <div><strong>Invoice No:</strong> <span style={{ fontSize: '1.15rem', fontWeight: 700 }}>{viewInvoice.invoice_number}</span></div>
                    <div style={{ textAlign: 'right' }}><strong>Invoice Date:</strong> {formatDate(viewInvoice.invoice_date)}</div>
                    <div><strong>Period:</strong> {formatDate(viewInvoice.period_start)} to {formatDate(viewInvoice.period_end)}</div>
                  </div>

                  <div className="customer-info" style={{ margin: '0.8rem 0' }}>
                    <strong>To: </strong>
                    <span style={{ fontWeight: 700 }}>
                      {viewInvoice.customer_name}{customer?.address ? ` (${customer.address})` : ''}
                    </span>
                  </div>

                  {(() => {
                    const invBills = viewInvoice.bills || [];
                    const hasChick = invBills.some(b => (b.chick_quantity || 0) > 0);
                    const hasFatwa = invBills.some(b => (b.fatwa_quantity || 0) > 0);
                    const hasOther = invBills.some(b => (b.custom_items || []).length > 0);
                    let buffaloMilk = 0, cowMilk = 0, chick = 0, fatwa = 0, custom = 0;
                    invBills.forEach(b => {
                      buffaloMilk += (b.morning_buffalo_milk || 0) + (b.evening_buffalo_milk || 0);
                      cowMilk += (b.morning_cow_milk || 0) + (b.evening_cow_milk || 0);
                      chick += (b.chick_quantity || 0) * (b.chick_price_per_unit || 0);
                      fatwa += (b.fatwa_quantity || 0) * (b.fatwa_price_per_unit || 0);
                      (b.custom_items || []).forEach(ci => { custom += ci.total || 0; });
                    });
                    const sortedBills = [...invBills].sort((a, b) => new Date(a.date) - new Date(b.date));
                    const tableClassName = `bill-table ${(hasChick || hasFatwa || hasOther) ? 'bill-table-compact' : ''}`;

                    return (
                      <table className={tableClassName}>
                        <thead>
                          <tr>
                            <th>Date</th>
                            <th colSpan="2">Buffalo (Morn / Night)</th>
                            <th colSpan="2">Cow (Morn / Night)</th>
                            <th>Buffalo Price</th>
                            <th>Cow Price</th>
                            {hasChick && (<><th>Chick Qty</th><th>Chick Price</th></>)}
                            {hasFatwa && (<><th>Fatwa Qty</th><th>Fatwa Price</th></>)}
                            {hasOther && <th>Other Items</th>}
                            <th>Amount (₹)</th>
                          </tr>
                        </thead>
                        <tbody>
                          {sortedBills.map(b => (
                            <tr key={b.bill_no}>
                              <td>{formatDate(b.date)}</td>
                              <td>{b.morning_buffalo_milk || '-'}</td>
                              <td>{b.evening_buffalo_milk || '-'}</td>
                              <td>{b.morning_cow_milk || '-'}</td>
                              <td>{b.evening_cow_milk || '-'}</td>
                              <td style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>₹{b.buffalo_milk_price || 0}/L</td>
                              <td style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>₹{b.cow_milk_price || 0}/L</td>
                              {hasChick && (
                                <>
                                  <td>{b.chick_quantity > 0 ? b.chick_quantity : '-'}</td>
                                  <td style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>{b.chick_quantity > 0 ? `₹${b.chick_price_per_unit || 0}` : '-'}</td>
                                </>
                              )}
                              {hasFatwa && (
                                <>
                                  <td>{b.fatwa_quantity > 0 ? b.fatwa_quantity : '-'}</td>
                                  <td style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>{b.fatwa_quantity > 0 ? `₹${b.fatwa_price_per_unit || 0}` : '-'}</td>
                                </>
                              )}
                              {hasOther && (
                                <td style={{ fontSize: '0.8rem' }}>
                                  {(b.custom_items || []).length > 0 ? b.custom_items.map(ci => `${ci.item_name} x${ci.quantity}`).join(', ') : '-'}
                                </td>
                              )}
                              <td>₹{(b.total_amount || 0).toFixed(2)}</td>
                            </tr>
                          ))}
                          <tr className="total-row">
                            <td><strong>TOTAL</strong></td>
                            <td colSpan="2"><strong>{buffaloMilk.toFixed(2)} L</strong></td>
                            <td colSpan="2"><strong>{cowMilk.toFixed(2)} L</strong></td>
                            <td colSpan="2"></td>
                            {hasChick && <td colSpan="2"><strong>₹{chick.toFixed(2)}</strong></td>}
                            {hasFatwa && <td colSpan="2"><strong>₹{fatwa.toFixed(2)}</strong></td>}
                            {hasOther && <td><strong>₹{custom.toFixed(2)}</strong></td>}
                            <td><strong>₹{(viewInvoice.total_amount || 0).toFixed(2)}</strong></td>
                          </tr>
                        </tbody>
                      </table>
                    );
                  })()}

                  <div className="totals-section" style={{ marginTop: '1rem' }}>
                    <div className="final-total" style={{ fontSize: '1.1rem', fontWeight: 700, borderTop: '2px solid #333', paddingTop: '0.5rem' }}>
                      Total Invoice Amount: ₹{(viewInvoice.total_amount || 0).toFixed(2)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  // Form View
  if (currentView === 'form') {
    return renderLayout(
      <>
        <div className="page-header">
           <div className="header-left">
            <button className="btn btn-secondary" onClick={handleCancel} style={{ marginRight: '1rem' }}>
              <ArrowLeft size={18} /> Cancel
            </button>
            <h1 className="page-title">
               {editingItem ? 'Edit Customer' : 'Add New Customer'}
            </h1>
           </div>
        </div>

        {error && (
            <div className="alert alert-error">
                <p>{error}</p>
            </div>
        )}

        <div className="card" style={{ maxWidth: '800px', margin: '0 auto' }}>
            <div className="form-row">
                 <div className="form-group">
                    <label htmlFor="name" className="form-label">Customer Name *</label>
                    <input
                        type="text"
                        id="name"
                        name="name"
                        className="form-input"
                        value={formData.name}
                        onChange={handleInputChange}
                        disabled={loading}
                    />
                </div>
                <div className="form-group">
                    <label htmlFor="phone_number" className="form-label">Phone Number</label>
                    <input
                        type="tel"
                        id="phone_number"
                        name="phone_number"
                        className="form-input"
                        value={formData.phone_number}
                        onChange={handleInputChange}
                        disabled={loading}
                    />
                </div>
            </div>

            <div className="form-row">
                <div className="form-group">
                    <label htmlFor="vehicle_number" className="form-label">Vehicle Number</label>
                    <input
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        id="vehicle_number"
                        name="vehicle_number"
                        className="form-input"
                        value={formData.vehicle_number}
                        onChange={(e) => {
                          const digitsOnly = e.target.value.replace(/\D/g, '');
                          setFormData(prev => ({ ...prev, vehicle_number: digitsOnly }));
                        }}
                        placeholder="Digits only, optional"
                        disabled={loading}
                    />
                </div>
            </div>

            <div className="form-group">
                <label htmlFor="address" className="form-label">Address</label>
                <textarea
                    id="address"
                    name="address"
                    className="form-textarea"
                    value={formData.address}
                    onChange={handleInputChange}
                    rows="3"
                    disabled={loading}
                />
            </div>

            <div className="card-header" style={{ borderTop: '1px solid var(--border-color)', paddingTop: '1.5rem', marginTop: '1rem' }}>
                 <h3 className="card-title" style={{ fontSize: '1.1rem' }}>Pricing & Billing</h3>
            </div>

            <div className="form-row">
                <div className="form-group">
                    <label htmlFor="cow_milk_price" className="form-label">Cow Milk Price (₹)</label>
                    <input
                        type="number"
                        id="cow_milk_price"
                        name="cow_milk_price"
                        className="form-input"
                        value={formData.cow_milk_price}
                        onChange={handleInputChange}
                        placeholder="Default"
                        disabled={loading}
                    />
                </div>
                 <div className="form-group">
                    <label htmlFor="buffalo_milk_price" className="form-label">Buffalo Milk Price (₹)</label>
                    <input
                        type="number"
                        id="buffalo_milk_price"
                        name="buffalo_milk_price"
                        className="form-input"
                        value={formData.buffalo_milk_price}
                        onChange={handleInputChange}
                        placeholder="Default"
                        disabled={loading}
                    />
                </div>
            </div>

            <div className="form-row">
                <div className="form-group">
                    <label htmlFor="chick_price" className="form-label">Chick Price (₹/unit)</label>
                    <input
                        type="number"
                        id="chick_price"
                        name="chick_price"
                        className="form-input"
                        value={formData.chick_price}
                        onChange={handleInputChange}
                        placeholder="Optional"
                        min="0"
                        step="0.01"
                        disabled={loading}
                    />
                </div>
                <div className="form-group">
                    <label htmlFor="fatwa_price" className="form-label">Fatwa Price (₹/unit)</label>
                    <input
                        type="number"
                        id="fatwa_price"
                        name="fatwa_price"
                        className="form-input"
                        value={formData.fatwa_price}
                        onChange={handleInputChange}
                        placeholder="Optional"
                        min="0"
                        step="0.01"
                        disabled={loading}
                    />
                </div>
            </div>

             <div className="form-row">
                <div className="form-group">
                    <label htmlFor="billing_period" className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <input
                            type="checkbox"
                            checked={formData.billing_period_enabled}
                            onChange={e => setFormData({ ...formData, billing_period_enabled: e.target.checked })}
                            disabled={loading}
                        />
                        Billing Period (Days)
                    </label>
                    <input
                        type="number"
                        id="billing_period"
                        name="billing_period"
                        className="form-input"
                        value={formData.billing_period}
                        onChange={handleInputChange}
                        disabled={loading || !formData.billing_period_enabled}
                    />
                </div>
                <div className="form-group">
                    <label htmlFor="payment_terms" className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <input
                            type="checkbox"
                            checked={formData.payment_terms_enabled}
                            onChange={e => setFormData({ ...formData, payment_terms_enabled: e.target.checked })}
                            disabled={loading}
                        />
                        Payment Terms (Days to pay)
                    </label>
                    <input
                        type="number"
                        id="payment_terms"
                        name="payment_terms"
                        className="form-input"
                        value={formData.payment_terms}
                        onChange={handleInputChange}
                        placeholder="30"
                        min="1"
                        disabled={loading || !formData.payment_terms_enabled}
                    />
                </div>
            </div>

            <div className="form-row">
                <div className="form-group">
                    <label htmlFor="payment_mode" className="form-label">Preferred Payment Mode</label>
                    <select
                        id="payment_mode"
                        name="payment_mode"
                        className="form-select"
                        value={formData.payment_mode}
                        onChange={handleInputChange}
                        disabled={loading}
                    >
                        <option value="cash">Cash</option>
                        <option value="bank">Bank</option>
                        <option value="upi">UPI</option>
                        <option value="both">Both (Cash + Bank)</option>
                    </select>
                </div>
            </div>

            <div className="form-actions" style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem', marginTop: '2rem' }}>
                <button type="button" className="btn btn-secondary" onClick={handleCancel} disabled={loading}>
                    Cancel
                </button>
                <button type="button" className="btn btn-primary" onClick={handleSubmit} disabled={loading}>
                     <Check size={18} />
                    {loading ? 'Saving...' : (editingItem ? 'Update' : 'Save Customer')}
                </button>
            </div>
        </div>
      </>
    );
  }

  // List View
  return renderLayout(
    <>
      <div className="page-header">
        <h1 className="page-title">
          Customer Management
        </h1>
        <button className="btn btn-primary" onClick={handleAdd}>
          <Plus size={18} /> Add Customer
        </button>
      </div>

      <div className="filter-bar">
        <div className="filter-group">
            <label className="form-label" style={{marginBottom: 0, fontSize: '0.8rem'}}>Customer ID</label>
            <input
                type="text"
                name="customer_id"
                className="form-input"
                value={filters.customer_id}
                onChange={handleFilterChange}
                placeholder="Search ID"
            />
        </div>
         <div className="filter-group" style={{ flex: 1.5 }}>
            <label className="form-label" style={{marginBottom: 0, fontSize: '0.8rem'}}>Name</label>
            <input
                type="text"
                name="name"
                className="form-input"
                value={filters.name}
                onChange={handleFilterChange}
                placeholder="Search Name"
            />
        </div>
        <div className="filter-group" style={{ flex: 1.5 }}>
            <label className="form-label" style={{marginBottom: 0, fontSize: '0.8rem'}}>Address</label>
            <input
                type="text"
                name="address"
                className="form-input"
                value={filters.address}
                onChange={handleFilterChange}
                placeholder="Search Address"
            />
        </div>
        <button className="btn btn-secondary" onClick={clearFilters} style={{ height: '42px', alignSelf: 'flex-end' }}>
          Clear
        </button>
      </div>

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Name</th>
              <th>Address</th>
              <th>Phone</th>
              <th>Vehicle</th>
              <th>Payment Mode</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {currentItems.map(item => (
              <tr key={item.customer_id}>
                <td>{item.customer_id}</td>
                <td style={{ fontWeight: 500 }}>{item.name}</td>
                <td>{item.address || '-'}</td>
                <td>{item.phone_number || '-'}</td>
                <td>{item.vehicle_number || '-'}</td>
                 <td>
                     <span className={`status-badge ${item.payment_mode === 'cash' ? 'available' : 'unavailable'}`}>
                        {item.payment_mode || 'cash'}
                    </span>
                 </td>
                <td>
                  <div className="actions-cell">
                    <button
                      className="btn-icon"
                      onClick={() => handleView(item)}
                      title="View Details"
                    >
                      <Eye size={18} />
                    </button>
                    <button
                      className="btn-icon"
                      onClick={() => handleEdit(item)}
                      title="Edit"
                    >
                      <Edit size={18} />
                    </button>
                    <button
                      className="btn-icon"
                      style={{ color: 'var(--error-color)' }}
                      onClick={() => handleDelete(item.customer_id)}
                      title="Delete"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

         {currentItems.length === 0 && !loading && (
          <div className="no-data">
            <p>No customers found matching your filters</p>
          </div>
        )}
      </div>

      {totalPages > 1 && (
        <div className="pagination">
          <button
            className="pagination-btn"
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
          >
            <ChevronLeft size={16} />
          </button>

          {[...Array(totalPages)].map((_, index) => (
            <button
              key={index + 1}
              className={`pagination-btn ${currentPage === index + 1 ? 'active' : ''}`}
              onClick={() => handlePageChange(index + 1)}
            >
              {index + 1}
            </button>
          ))}

          <button
            className="pagination-btn"
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            <ChevronRight size={16} />
          </button>
        </div>
      )}
       <div style={{ textAlign: 'center', marginTop: '1rem', color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
        Showing {currentItems.length} of {filteredData.length} customers
      </div>
    </>
  );
}

export default CustomerPage;